import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Monitor,
  Smartphone,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ExternalLink,
  ArrowLeft,
  Zap,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../services/api';

const PRODUCT_THEME = {
  'New Gear': 'bg-blue-50 text-blue-700',
  'Used Gear': 'bg-purple-50 text-purple-700',
  Connect: 'bg-emerald-50 text-emerald-700',
};

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function StatusIcon({ status }) {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4 text-success-600" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
  return <MinusCircle className="h-4 w-4 text-gray-400" />;
}

function StatCard({ label, value, tone }) {
  return (
    <div className={clsx('rounded-xl border p-3', tone)}>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-xs opacity-70">{label}</p>
    </div>
  );
}

function HistoryRow({ h, runHref }) {
  const [open, setOpen] = useState(false);
  const failed = h.status === 'failed';
  const hasRaw = failed && h.error_raw;

  return (
    <li className="px-5 py-3 flex items-start gap-3">
      <StatusIcon status={h.status} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={runHref}
            className="text-sm font-medium text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            Run #{h.run_id} <ExternalLink className="h-3 w-3 text-gray-300" />
          </Link>
          <span className="text-xs text-gray-400">{fmtDate(h.timestamp)}</span>
          <span
            className={clsx(
              'text-[11px] font-medium capitalize',
              h.status === 'passed' && 'text-success-600',
              h.status === 'failed' && 'text-red-500',
              h.status === 'skipped' && 'text-gray-400'
            )}
          >
            {h.status}
          </span>
          <span className="text-[11px] text-gray-400">· {fmtDuration(h.duration)}</span>
        </div>

        {failed && h.error && <p className="mt-1 text-xs text-red-600/90">{h.error}</p>}

        {hasRaw && (
          <>
            <button
              onClick={() => setOpen((o) => !o)}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-800"
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {open ? 'Hide technical details' : 'View technical details'}
            </button>
            {open && (
              <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-[11px] leading-relaxed p-3 whitespace-pre-wrap break-words">
                {h.error_raw}
              </pre>
            )}
          </>
        )}
      </div>
    </li>
  );
}

const TestCaseHistory = () => {
  const [params] = useSearchParams();
  const source = params.get('source') || 'web';
  const title = params.get('title') || '';
  const file_path = params.get('file_path') || '';
  const suite = params.get('suite') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiService
      .getTestCaseHistory({ source, title, file_path, suite })
      .then((res) => {
        if (alive) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((e) => alive && setError(e.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [source, title, file_path, suite]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-gray-600">{error}</div>;
  }

  const meta = data?.meta || {};
  const stats = data?.stats || {};
  const history = data?.history || [];
  const Icon = source === 'mobile' ? Smartphone : Monitor;

  return (
    <div className="space-y-6">
      <Link to="/test-runs" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Back to test runs
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-gray-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900">{meta.title || title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className="uppercase tracking-wide text-gray-400">
                {source === 'mobile' ? 'Mobile app' : 'Web'}
              </span>
              {meta.product && (
                <span
                  className={clsx(
                    'px-1.5 py-0.5 rounded text-[11px] font-medium',
                    PRODUCT_THEME[meta.product] || 'bg-gray-100 text-gray-600'
                  )}
                >
                  {meta.product}
                </span>
              )}
              {meta.journey && <span className="text-gray-400">{meta.journey}</span>}
              {stats.flaky && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-700">
                  <Zap className="h-3 w-3" /> Flaky
                </span>
              )}
            </div>
            {meta.summary && <p className="mt-2 text-sm text-gray-600">{meta.summary}</p>}
            {(meta.suite || meta.file_path) && (
              <p className="mt-1 text-[11px] text-gray-400 truncate" title={meta.file_path || meta.suite}>
                {meta.file_path || meta.suite}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Runs" value={stats.total ?? 0} tone="border-gray-200 bg-white text-gray-800" />
        <StatCard label="Passed" value={stats.passed ?? 0} tone="border-success-100 bg-success-50 text-success-700" />
        <StatCard label="Failed" value={stats.failed ?? 0} tone="border-red-100 bg-red-50 text-red-600" />
        <StatCard label="Skipped" value={stats.skipped ?? 0} tone="border-gray-200 bg-gray-50 text-gray-500" />
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-800">Run history</h2>
        </div>
        {history.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-500">
            No run history found for this test case.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.map((h) => {
              const runHref = source === 'mobile' ? `/mobile-tests/${h.run_id}` : `/test-runs/${h.run_id}`;
              return <HistoryRow key={h.run_id} h={h} runHref={runHref} />;
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TestCaseHistory;
